import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutGrid,
  Settings,
  Menu as MenuIcon,
  X,
  ChevronRight,
  ChevronDown,
  Check,
  Building2,
  Users,
  LogOut,
  User,
  ScrollText,
  HelpCircle,
  Layers,
  Shield,
  ShieldCheck,
  Rocket,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { useChannel } from '../context/ChannelContext';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import PlanBadge from './PlanBadge';
import ProDialog from './ProDialog';
import { useCampaignsConfig } from '../hooks/useCampaignsConfig';

export const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { channels, selectedChannel, switchChannel, loading: channelLoading } = useChannel();
  const { user, isAdmin, logout } = useAuth();
  const {
    workspaces, selectedWorkspace, selectWorkspace,
    isWorkspaceAdmin, loading: wsLoading
  } = useWorkspace();

  const campaignsConfig = useCampaignsConfig();
  const campaignsProGated = !campaignsConfig.allowed && campaignsConfig.reason === 'Campaigns are a Pro feature';

  const closeSidebar = () => setIsOpen(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
    closeSidebar();
  };

  const canManageUsers = isAdmin || isWorkspaceAdmin;

  const channelNavItems = [
    { path: '/menus', label: 'Menus', icon: LayoutGrid },
    ...(campaignsConfig.visible ? [{ path: '/campaigns', label: 'Campaigns', icon: Rocket, proGated: campaignsProGated }] : []),
  ];

  const workspaceNavItems = [
    { path: '/channels', label: 'Channels', icon: Settings },
    ...(canManageUsers ? [{ path: '/users', label: 'Users', icon: Users }] : []),
    ...(isWorkspaceAdmin && !isAdmin ? [{ path: '/logs', label: 'Activity Logs', icon: ScrollText }] : []),
    { path: '/help', label: 'Help', icon: HelpCircle },
  ];

  const adminNavItems = isAdmin ? [
    { path: '/admin', label: 'Admin Dashboard', icon: ShieldCheck },
    { path: '/all-users', label: 'All Users', icon: Users },
    { path: '/workspaces', label: 'Workspaces', icon: Layers },
    { path: '/logs', label: 'Activity Logs', icon: ScrollText },
  ] : [];

  const WorkspaceSelector = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-between bg-[#18181B] border-[#27272A] hover:bg-[#27272A] text-left"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Layers className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <span className="truncate text-sm">
              {selectedWorkspace?.name || 'Select Workspace'}
            </span>
          </div>
          <ChevronDown className="w-4 h-4 text-[#52525B] flex-shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[220px] bg-[#18181B] border-[#27272A]"
      >
        {workspaces.length === 0 ? (
          <div className="px-3 py-4 text-center">
            <p className="text-sm text-[#52525B]">No workspaces</p>
          </div>
        ) : (
          <>
            {workspaces.map((ws) => (
              <DropdownMenuItem
                key={ws.id}
                onClick={() => {
                  selectWorkspace(ws.id);
                  closeSidebar();
                }}
                className="focus:bg-[#27272A] cursor-pointer"
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                    <span className="truncate text-sm">{ws.name}</span>
                    {ws.myRole === 'admin' && (
                      <Shield className="w-3 h-3 text-amber-400 flex-shrink-0" />
                    )}
                  </div>
                  {selectedWorkspace?.id === ws.id && (
                    <Check className="w-4 h-4 text-[#00E05E] flex-shrink-0" />
                  )}
                </div>
              </DropdownMenuItem>
            ))}
            {isAdmin && (
              <>
                <DropdownMenuSeparator className="bg-[#27272A]" />
                <DropdownMenuItem asChild className="focus:bg-[#27272A]">
                  <NavLink to="/workspaces" onClick={closeSidebar} className="cursor-pointer">
                    <Layers className="w-4 h-4 mr-2" />
                    Manage Workspaces
                  </NavLink>
                </DropdownMenuItem>
              </>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const ChannelSelector = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-between bg-[#18181B] border-[#27272A] hover:bg-[#27272A] text-left"
          data-testid="channel-selector"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="w-4 h-4 text-[#06C755] flex-shrink-0" />
            <span className="truncate">
              {selectedChannel?.name || 'Select Channel'}
            </span>
          </div>
          <ChevronDown className="w-4 h-4 text-[#52525B] flex-shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[200px] bg-[#18181B] border-[#27272A]"
      >
        {channels.length === 0 ? (
          <div className="px-3 py-4 text-center">
            <p className="text-sm text-[#52525B]">No channels configured</p>
            <NavLink
              to="/channels"
              onClick={closeSidebar}
              className="text-xs text-[#00E05E] hover:underline mt-1 block"
            >
              Add a channel
            </NavLink>
          </div>
        ) : (
          <>
            {channels.map((channel) => (
              <DropdownMenuItem
                key={channel.id}
                onClick={() => switchChannel(channel.id)}
                className="focus:bg-[#27272A] cursor-pointer"
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={cn(
                      "w-2 h-2 rounded-full flex-shrink-0",
                      channel.isValid ? "bg-[#06C755]" : "bg-yellow-500"
                    )} />
                    <span className="truncate">{channel.name}</span>
                  </div>
                  {selectedChannel?.id === channel.id && (
                    <Check className="w-4 h-4 text-[#00E05E] flex-shrink-0" />
                  )}
                </div>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator className="bg-[#27272A]" />
            <DropdownMenuItem asChild className="focus:bg-[#27272A]">
              <NavLink to="/channels" onClick={closeSidebar} className="cursor-pointer">
                <Settings className="w-4 h-4 mr-2" />
                Manage Channels
              </NavLink>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <>
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#09090B] border-b border-[#27272A] px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => setIsOpen(true)}
          data-testid="mobile-menu-btn"
          className="w-8 h-8 rounded-lg bg-[#00E05E] flex items-center justify-center flex-shrink-0"
          aria-label="Open menu"
        >
          <MenuIcon className="w-4 h-4 text-black" />
        </button>
        <button
          onClick={() => { navigate(isAdmin ? '/workspaces' : '/menus'); }}
          className="font-bold hover:text-[#00E05E] transition-colors"
        >
          LINE Menu
        </button>
      </div>

      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:static inset-y-0 left-0 z-50 w-64 flex flex-col bg-[#09090B] border-r border-[#27272A] transform transition-transform duration-200 ease-out lg:h-screen",
          "lg:transform-none",
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-4 border-b border-[#27272A] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#00E05E] flex items-center justify-center">
              <MenuIcon className="w-5 h-5 text-black" />
            </div>
            <div>
              <h1 className="font-bold text-lg tracking-tight">LINE Menu</h1>
              <p className="text-xs text-[#A1A1AA]">Rich Menu Builder</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={closeSidebar}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Workspace Selector */}
        <div className="p-4 border-b border-[#27272A]">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-[#52525B] uppercase tracking-wider">Workspace</p>
            <PlanBadge />
          </div>
          {wsLoading ? (
            <div className="h-9 bg-[#18181B] rounded-md animate-pulse" />
          ) : (
            <WorkspaceSelector />
          )}
        </div>

        {/* Channel Selector */}
        <div className="p-4 border-b border-[#27272A]">
          <p className="label-text mb-2">Channel</p>
          {channelLoading ? (
            <div className="h-10 bg-[#18181B] rounded-md animate-pulse" />
          ) : (
            <ChannelSelector />
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto flex flex-col gap-4">
          {/* Channel-context nav */}
          <div>
            <p className="text-[10px] text-[#3F3F46] uppercase tracking-wider px-4 mb-1.5">Channel</p>
            <ul className="space-y-1">
              {channelNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path ||
                  (item.path === '/menus' && (location.pathname === '/' || location.pathname === '/create' ||
                    location.pathname.startsWith('/edit/'))) ||
                  (item.path === '/campaigns' && location.pathname.startsWith('/campaigns/'));
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      onClick={closeSidebar}
                      data-testid={`nav-${item.path.slice(1)}`}
                      className={cn(
                        "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                        isActive
                          ? "bg-[#00E05E]/10 text-[#00E05E] border border-[#00E05E]/20"
                          : "text-[#A1A1AA] hover:bg-white/5 hover:text-white"
                      )}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                      {item.proGated && (
                        <span className="ml-auto text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">Pro</span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Workspace nav */}
          <div>
            <p className="text-[10px] text-[#3F3F46] uppercase tracking-wider px-4 mb-1.5">Workspace</p>
            <ul className="space-y-1">
              {workspaceNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path ||
                  location.pathname.startsWith(item.path + '/');
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      onClick={closeSidebar}
                      data-testid={`nav-${item.path.slice(1)}`}
                      className={cn(
                        "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                        isActive
                          ? "bg-[#00E05E]/10 text-[#00E05E] border border-[#00E05E]/20"
                          : "text-[#A1A1AA] hover:bg-white/5 hover:text-white"
                      )}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Admin section */}
          {adminNavItems.length > 0 && (
            <div>
              <p className="text-[10px] text-[#3F3F46] uppercase tracking-wider px-4 mb-1 flex items-center gap-1.5">
                <ShieldCheck className="w-3 h-3" />
                Super Admin
              </p>
              <ul className="space-y-1">
                {adminNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        onClick={closeSidebar}
                        data-testid={`nav-${item.path.slice(1)}`}
                        className={cn(
                          "flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200",
                          isActive
                            ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                            : "text-[#52525B] hover:bg-white/5 hover:text-[#A1A1AA]"
                        )}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="font-medium text-sm">{item.label}</span>
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </nav>

        {/* Footer - User Info */}
        <div className="p-4 border-t border-[#27272A]">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-start gap-3 px-3 py-2.5 h-auto hover:bg-[#18181B]"
              >
                <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-emerald-500" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name || 'User'}</p>
                  <p className="text-xs text-[#52525B] truncate">{user?.email}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-[#52525B] flex-shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              side="top"
              className="w-[200px] bg-[#18181B] border-[#27272A]"
            >
              <div className="px-3 py-2">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-[#52525B] capitalize">{user?.role}</p>
              </div>
              <DropdownMenuSeparator className="bg-[#27272A]" />
              <DropdownMenuItem asChild className="focus:bg-[#27272A] cursor-pointer">
                <NavLink to="/settings" onClick={closeSidebar}>
                  <Settings className="w-4 h-4 mr-2" />
                  Account Settings
                </NavLink>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[#27272A]" />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-400 focus:bg-red-500/10 focus:text-red-400 cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <p className="text-center text-[10px] text-zinc-700 mt-2 select-none">
            v{require('../../package.json').version}
          </p>
        </div>
      </aside>
      <ProDialog />
    </>
  );
};

export default Sidebar;
